import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox as InboxIcon, Plus, Search, Settings } from 'lucide-react';
import { crmConversationsApi, crmMailboxesApi } from '../../api/crm';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useSocket } from '../../contexts/SocketContext';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import ConversationList from '../../components/crm/ConversationList';
import ComposeEmailModal from '../../components/crm/ComposeEmailModal';

type View = 'needsReply' | 'unread' | 'mine' | 'unassigned' | 'active' | 'waiting' | 'closed' | 'spam';

const VIEWS: { id: View; label: string; help: string }[] = [
  { id: 'needsReply', label: 'Needs reply', help: 'They wrote last and nobody has answered yet.' },
  { id: 'unread', label: 'Unread', help: 'New messages nobody has opened.' },
  { id: 'mine', label: 'Mine', help: 'Conversations you own.' },
  { id: 'unassigned', label: 'Unassigned', help: 'Nobody owns these yet — pick one up.' },
  { id: 'active', label: 'All active', help: 'Everything that is not closed or spam.' },
  { id: 'waiting', label: 'Waiting on them', help: 'We replied last; the ball is in their court.' },
  { id: 'closed', label: 'Closed', help: 'Done. A new message from them reopens it.' },
  { id: 'spam', label: 'Spam', help: 'Marked as spam. Delete or reopen from inside.' },
];

export default function Inbox() {
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const { isAdmin } = usePermissions();
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const myId = (user as any)?.id ?? (user as any)?._id;

  const [view, setView] = useState<View>('needsReply');
  const [search, setSearch] = useState('');
  const [mailboxId, setMailboxId] = useState('');
  const [page, setPage] = useState(1);
  const [composing, setComposing] = useState(false);

  const params: Record<string, string | number> = { page, limit: 50 };
  if (view === 'needsReply') { params.status = 'active'; params.needsReply = '1'; }
  else if (view === 'unread') { params.status = 'active'; params.unread = '1'; }
  else if (view === 'mine') { params.status = 'active'; params.ownerId = myId; }
  else if (view === 'unassigned') { params.status = 'active'; params.unassigned = '1'; }
  else params.status = view;
  if (search.trim()) params.search = search.trim();
  if (mailboxId) params.mailboxId = mailboxId;

  const { data, isLoading } = useQuery({
    queryKey: ['crm-conversations', cid, params],
    queryFn: () => crmConversationsApi.list(params),
    enabled: !!cid,
  });
  const { data: mailboxes } = useQuery({
    queryKey: ['crm-mailboxes', cid],
    queryFn: () => crmMailboxesApi.list(),
    enabled: !!cid,
  });

  // Live: anything arriving in the inbox refreshes the list.
  useEffect(() => {
    if (!socket) return;
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['crm-conversations'] });
    socket.on('crm:conversation', refresh);
    return () => { socket.off('crm:conversation', refresh); };
  }, [socket, queryClient]);

  const counts = data?.counts;
  const countFor = (v: View): number | undefined => {
    if (!counts) return undefined;
    if (v === 'needsReply') return counts.needsReply;
    if (v === 'unread') return counts.unread;
    if (v === 'mine') return counts.mine;
    if (v === 'unassigned') return counts.unassigned;
    if (v === 'active') return counts.open + counts.waiting;
    if (v === 'waiting') return counts.waiting;
    return undefined;
  };

  const noMailboxes = mailboxes && mailboxes.length === 0;
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Inbox"
        intro="Sales email and contact-form leads, in one place. Reply from here and their answer comes back to the same thread. Start with Needs reply."
        actions={
          <>
            {isAdmin && (
              <Link to="/settings/company?tab=crm" className="btn btn-secondary inline-flex items-center gap-1.5" title="Mailboxes, pipeline stages and the lead form endpoint">
                <Settings className="w-4 h-4" /> CRM settings
              </Link>
            )}
            <button type="button" className="btn btn-primary inline-flex items-center gap-1.5" onClick={() => setComposing(true)}>
              <Plus className="w-4 h-4" /> New email
            </button>
          </>
        }
      />

      {noMailboxes && (
        <div className="card border-warn/40" style={{ borderColor: 'color-mix(in srgb, var(--scpa-status-warning) 50%, transparent)' }}>
          <p className="text-sm text-fg font-medium">No mailbox yet</p>
          <p className="text-sm text-muted mt-1">
            To get sales@ or info@ flowing in here, add it as a CRM mailbox
            {isAdmin ? <> under <Link to="/settings/company?tab=crm" className="text-primary hover:underline">Company Settings → CRM</Link></> : ' (an admin can do this under Company Settings → CRM)'}.
            Contact-form leads can also be posted straight in through the lead API — the setup steps are on the same page.
          </p>
        </div>
      )}

      {/* Views */}
      <div className="flex flex-wrap gap-2">
        {VIEWS.map(v => {
          const n = countFor(v.id);
          const on = view === v.id;
          return (
            <button
              key={v.id}
              type="button"
              title={v.help}
              onClick={() => { setView(v.id); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? 'bg-primary text-white border-primary' : 'bg-surface text-muted border-line hover:text-fg'}`}
            >
              {v.label}{n !== undefined && n > 0 && <span className={`ml-1.5 ${on ? 'opacity-90' : 'text-fg'}`}>{n}</span>}
              {v.id === 'needsReply' && counts && counts.overdue > 0 && (
                <span className={`ml-1.5 text-[10px] font-bold ${on ? 'text-white' : 'text-err'}`} title="Waiting more than a day">({counts.overdue} overdue)</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            className="input pl-9 w-full"
            placeholder="Search by name, email, subject or CRM number…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        {mailboxes && mailboxes.length > 1 && (
          <select className="select w-48" value={mailboxId} onChange={e => { setMailboxId(e.target.value); setPage(1); }} title="Only show one mailbox">
            <option value="">All mailboxes</option>
            {mailboxes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="card h-16 animate-pulse" />)}</div>
      ) : (
        <ConversationList
          conversations={data?.conversations ?? []}
          empty={
            view === 'needsReply'
              ? <EmptyState icon={<InboxIcon className="w-8 h-8" />}>Nothing is waiting on you. Nice.</EmptyState>
              : <EmptyState icon={<InboxIcon className="w-8 h-8" />}>Nothing here{search ? ' for that search' : ''}.</EmptyState>
          }
        />
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted">
          <span>{total} conversations</span>
          <div className="flex gap-2">
            <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span className="self-center">Page {page} of {pages}</span>
            <button className="btn btn-secondary" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      )}

      <ComposeEmailModal open={composing} onClose={() => setComposing(false)} />
    </div>
  );
}
